import { Skeleton, Typography } from '@neo4j-ndl/react';
import {
  BuildingOffice2IconOutline,
  ChevronRightIconOutline,
  FolderIconOutline,
  UserGroupIconOutline,
} from '@neo4j-ndl/react/icons';
import { ACTION, type Organization, consoleApi, usePermissions } from '@nx/state';
import { skipToken } from '@reduxjs/toolkit/query';
import { forwardRef } from 'react';
import { useNavigate } from 'react-router';

import { NewCountIndicator } from '../projects/project-card';

type OrgCardProps = {
  org: Organization;
  ref: React.RefObject<HTMLDivElement>;
};

export const OrgCard = forwardRef<HTMLDivElement, OrgCardProps>(({ org }, ref) => {
  const navigate = useNavigate();
  const { isAllowed } = usePermissions();

  const canReadOrgUsers = isAllowed(ACTION.READ, `organizations/${org.id}/users`);

  const { data: projects = [], isLoading: isLoadingProjects } = consoleApi.useListOrganizationProjectsQuery(
    org.id ? { organizationId: org.id } : skipToken,
  );
  const { data: members = [], isLoading: isLoadingMembers } = consoleApi.useListOrganizationMembersQuery(org.id, {
    skip: !canReadOrgUsers,
  });

  if (isLoadingProjects || isLoadingMembers) {
    return <Skeleton className="rounded-1xl h-[110px] w-full" onBackground="weak" />;
  }

  const projectsCount = projects.length;
  const membersCount = members.length;

  const onClick = () => {
    void navigate(`/org/${org.id}/projects`);
  };

  return (
    <div
      ref={ref}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onClick();
        }
      }}
      className="focus-visible:outline-palette-primary-focus border-neutral-border-weak rounded-1xl bg-neutral-bg-weak hover:border-neutral-border-strong flex flex-row items-start gap-6 border p-6 outline-none hover:cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2"
    >
      <div className="bg-palette-neutral-bg-strong flex flex-row items-center rounded-md p-2">
        <BuildingOffice2IconOutline className="text-palette-neutral-icon size-5" />
      </div>
      <div className="flex grow flex-col gap-2 overflow-hidden">
        <div className="flex w-full flex-col items-start gap-1">
          <Typography variant="h5">{org.displayName}</Typography>
        </div>
        <div className="flex w-full flex-row items-center gap-3">
          <NewCountIndicator
            icon={<FolderIconOutline className="text-palette-neutral-icon size-6 shrink-0" />}
            count={projectsCount}
            label={'Projects'}
          />
          <NewCountIndicator
            icon={<UserGroupIconOutline className="text-palette-neutral-icon size-6 shrink-0" />}
            count={membersCount}
            label={'Members'}
          />
          <ChevronRightIconOutline className="text-palette-neutral-icon ml-auto size-5 shrink-0" />
        </div>
      </div>
    </div>
  );
});
OrgCard.displayName = 'OrgCard';
